import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';
import { TranslateService } from '@ngx-translate/core';
import { DateFnsTimeFormat, TIME_PREF_12H } from 'src/app/enums/timePreference.enum';
import { AnalystChartXAxisKind } from 'src/app/enums/analystChartXAxisKind.enum';
import { AnalystHeatmapCategoryKind } from 'src/app/enums/analystHeatmapCategoryKind.enum';
import { AnalystChart, AnalystChartHeatmap, AnalystChartType, Citation } from 'src/app/models/analyst-output.model';
import { ChartService } from 'src/app/services/chart.service';
import { CitationPanelService } from 'src/app/services/citation-panel.service';
import { CommonService } from 'src/app/services/common.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import {
  ANALYST_CHART_BASE_HEIGHT,
  analystChartHostHeight,
  buildSharedTooltipText,
  escapeChartMarkup,
  formatHeatmapCategory,
  LEGEND_MAX_HEIGHT,
  legendAwareHostHeight,
} from 'src/app/shared/utils/analyst-chart-display.util';
import {
  axisNeedsYear,
  dateAxisEdgeInset,
  dateAxisMinGridDistance,
  deriveDateAxisBucketing,
  parseAnalystChartDate,
  renderableDateAxisUnits,
  resolveAnalystChartXAxisKind,
} from 'src/app/shared/utils/analyst-chart-axis.util';
import { capCitationMarkers, CitationMarker, CitationNumbering, EMPTY_CITATION_NUMBERING, resolveCitationMarkers } from 'src/app/shared/utils/citation-resolver.util';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

type ChartDataRow = Record<string, string | number>;
type AnalystXAxis = am5xy.CategoryAxis<am5xy.AxisRendererX> | am5xy.DateAxis<am5xy.AxisRendererX>;

interface DateChartData {
  rows: ChartDataRow[];
  timestamps: number[];
}

interface AxisDateFormats {
  second: string;
  hour: string;
  minute: string;
  day: string;
  fullDate: string;
}

type DateFormatsByUnit = Partial<Record<am5.time.TimeUnit, string>>;

interface DateAxisLabelFormats {
  dateFormats: DateFormatsByUnit;
  periodChangeDateFormats: DateFormatsByUnit;
}

interface SeriesBuildContext {
  root: am5.Root;
  am5Ns: typeof import('@amcharts/amcharts5');
  am5xyNs: typeof import('@amcharts/amcharts5/xy');
  xyChart: am5xy.XYChart;
  xAxis: AnalystXAxis;
  valueAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
  useDateAxis: boolean;
  tooltipText: string;
  sharedTooltip: boolean;
  chartData: ChartDataRow[];
  lineGapSettings: { autoGapCount?: number };
}

@Component({
  standalone: false,
  selector: 'app-analyst-chart',
  templateUrl: './analyst-chart.component.html',
  styleUrls: ['./analyst-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalystChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  private static readonly MAX_CATEGORY_LABEL_WIDTH = 90;
  private static readonly CATEGORY_MIN_GRID_DISTANCE = 30;
  private static readonly HEATMAP_MIN_GRID_DISTANCE = 20;
  private static readonly HEATMAP_X_MIN_GRID_DISTANCE = 56;
  private static readonly SHARED_ANCHOR_KEY = 'sharedAnchor';
  private static readonly SHARED_TOOLTIP_TEXT_KEY = 'sharedTooltipText';

  @Input() chart?: AnalystChart;
  @Input() citationNumbering: CitationNumbering = EMPTY_CITATION_NUMBERING;

  @ViewChild('chartDiv') chartDiv?: ElementRef<HTMLElement>;

  readonly trackByIndex = trackByIndex;

  hasPoints = false;
  renderedPointCount = 0;
  hostHeight = ANALYST_CHART_BASE_HEIGHT;
  seriesLabels: string[] = [];
  seriesMarkers: CitationMarker[][] = [];
  visibleSeriesMarkers: CitationMarker[][] = [];
  hiddenSeriesMarkers: CitationMarker[][] = [];

  private root?: am5.Root;
  private viewReady = false;
  private destroyed = false;

  constructor(
    private readonly chartService: ChartService,
    private readonly zone: NgZone,
    private readonly citationPanelService: CitationPanelService,
    private readonly dateFormatService: DateFormatService,
    private readonly translateService: TranslateService,
    private readonly commonService: CommonService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chart'] || changes['citationNumbering']) {
      this.recomputeCitations();
    }
    if (changes['chart']) {
      this.recomputeChartShape();
    }
    if (this.viewReady && changes['chart']) {
      void this.buildChart();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.buildChart();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disposeChart();
  }

  onMarkerClick(event: MouseEvent, citation: Citation): void {
    this.citationPanelService.openMarkerFromClick(event, citation);
  }

  private recomputeChartShape(): void {
    const chart = this.chart;
    if (!chart) {
      this.hasPoints = false;
      this.renderedPointCount = 0;
      return;
    }
    const heatmap = chart.heatmap;
    this.hasPoints = heatmap
      ? heatmap.values.some((row) => row.some((value) => value !== null))
      : chart.series.some((series) => series.points.length > 0);
    this.renderedPointCount = heatmap
      ? heatmap.xCategories.length * heatmap.yCategories.length
      : chart.series.reduce((sum, series) => sum + series.points.length, 0);
    this.hostHeight = analystChartHostHeight(chart);
    this.seriesLabels = heatmap ? [heatmap.unit || chart.title] : chart.series.map((series) => series.name);
  }

  private recomputeCitations(): void {
    if (!this.chart) {
      this.seriesMarkers = [];
      this.visibleSeriesMarkers = [];
      this.hiddenSeriesMarkers = [];
      return;
    }
    const citationGroups = this.chart.heatmap ? [this.chart.heatmap.citationIds] : this.chart.series.map((series) => series.citationIds);
    this.seriesMarkers = citationGroups.map((citationIds) => resolveCitationMarkers(citationIds, this.citationNumbering));
    const capped = this.seriesMarkers.map((markers) => capCitationMarkers(markers));
    this.visibleSeriesMarkers = capped.map(({ visible }) => visible);
    this.hiddenSeriesMarkers = capped.map(({ hidden }) => hidden);
  }

  private async buildChart(): Promise<void> {
    const chart = this.chart;

    if (!this.isChartBuildable(chart)) {
      this.disposeChart();
      return;
    }

    const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
    if (this.chart !== chart || !this.isChartBuildable(chart)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.disposeChart();

      const host = this.chartDiv?.nativeElement;
      if (!host) {
        return;
      }

      if (chart.chartType === AnalystChartType.Heatmap && chart.heatmap) {
        this.root = this.buildHeatmapChart(am5Ns, am5xyNs, host, chart, chart.heatmap);
        return;
      }

      const xValues = this.getXValues(chart);
      const stampedKind = resolveAnalystChartXAxisKind(chart);
      const dateData = stampedKind === AnalystChartXAxisKind.Category ? null : this.buildDateChartData(chart, xValues);
      const useDateAxis = dateData !== null;
      const dateOnly = useDateAxis && stampedKind === AnalystChartXAxisKind.Date;
      const chartData = dateData ? dateData.rows : this.buildCategoryChartData(chart, xValues);
      const sharedTooltip = chart.series.length > 1;
      if (sharedTooltip) {
        this.addSharedTooltipFields(chartData, chart);
      }
      const userTimeZone = useDateAxis ? this.dateFormatService.getTimeZone() : 'UTC';
      const renderTimeZone = dateOnly ? 'UTC' : userTimeZone;
      const bucketing = dateData ? deriveDateAxisBucketing(dateData.timestamps, renderTimeZone) : null;
      const showYear = !!dateData && axisNeedsYear(dateData.timestamps, renderTimeZone, userTimeZone);
      const dateFormats = this.axisDateFormats(showYear);
      const labelFormats = this.dateAxisLabelFormats(dateFormats, showYear);
      const axisLabelFormats = dateData && bucketing
        ? this.renderableLabelFormats(labelFormats, renderableDateAxisUnits(dateData.timestamps, bucketing.baseInterval))
        : null;

      const root = this.chartService.createRoot(host);
      if (dateOnly) {
        root.utc = true;
      } else if (useDateAxis) {
        root.timezone = am5Ns.Timezone.new(userTimeZone);
      }

      const xyChart = root.container.children.push(am5xyNs.XYChart.new(root, { layout: root.verticalLayout }));
      if (axisLabelFormats) {
        this.reserveEdgeLabelRoom(xyChart, dateAxisEdgeInset(axisLabelFormats));
      }

      const xRenderer = am5xyNs.AxisRendererX.new(root, {
        minGridDistance: axisLabelFormats
          ? dateAxisMinGridDistance(axisLabelFormats)
          : AnalystChartComponent.CATEGORY_MIN_GRID_DISTANCE,
      });
      xRenderer.grid.template.set('location', 0);

      let xAxis: AnalystXAxis;

      if (bucketing) {
        const dateAxis = xyChart.xAxes.push(am5xyNs.DateAxis.new(root, {
          baseInterval: bucketing.baseInterval,
          renderer: xRenderer,
        })) as am5xy.DateAxis<am5xy.AxisRendererX>;
        dateAxis.set('dateFormats', { ...dateAxis.get('dateFormats'), ...labelFormats.dateFormats });
        dateAxis.set('periodChangeDateFormats', {
          ...dateAxis.get('periodChangeDateFormats'),
          ...labelFormats.periodChangeDateFormats,
        });
        xAxis = dateAxis;
      } else {
        xRenderer.labels.template.setAll({
          oversizedBehavior: 'wrap',
          maxWidth: AnalystChartComponent.MAX_CATEGORY_LABEL_WIDTH,
          textAlign: 'center',
        });
        const categoryAxis = xyChart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
          categoryField: 'x',
          renderer: xRenderer,
        })) as am5xy.CategoryAxis<am5xy.AxisRendererX>;
        categoryAxis.data.setAll(chartData);
        xAxis = categoryAxis;
      }

      xAxis.children.push(am5Ns.Label.new(root, {
        text: escapeChartMarkup(chart.xAxisLabel),
        x: am5Ns.p50,
        centerX: am5Ns.p50,
      }));

      const valueAxis = xyChart.yAxes.push(am5xyNs.ValueAxis.new(root, {
        renderer: am5xyNs.AxisRendererY.new(root, {}),
      })) as am5xy.ValueAxis<am5xy.AxisRendererY>;
      this.applyValueAxisDomain(valueAxis, chart);
      valueAxis.children.unshift(am5Ns.Label.new(root, {
        rotation: -90,
        text: escapeChartMarkup(this.valueAxisTitle(chart)),
        y: am5Ns.p50,
        centerX: am5Ns.p50,
      }));

      const context: SeriesBuildContext = {
        root,
        am5Ns,
        am5xyNs,
        xyChart,
        xAxis,
        valueAxis,
        useDateAxis,
        tooltipText: this.seriesTooltipText(useDateAxis, dateOnly, dateFormats),
        sharedTooltip,
        chartData,
        lineGapSettings: bucketing ? { autoGapCount: bucketing.autoGapCount } : {},
      };
      const createdSeries = chart.series.map((series, index) => this.addSeries(context, chart.chartType, series, index));
      if (sharedTooltip) {
        const anchor = this.addSharedTooltipAnchor(context, this.tooltipDateHeader(useDateAxis, dateOnly, dateFormats));
        this.syncSharedTooltipWithVisibility(chart, chartData, createdSeries, anchor);
      }

      xyChart.set('cursor', am5xyNs.XYCursor.new(root, { behavior: 'none', xAxis }));

      if (sharedTooltip) {
        const legend = xyChart.children.push(am5Ns.Legend.new(root, {
          centerX: am5Ns.p50,
          x: am5Ns.p50,
          maxHeight: LEGEND_MAX_HEIGHT,
          verticalScrollbar: am5Ns.Scrollbar.new(root, { orientation: 'vertical' }),
        }));
        legend.data.setAll(createdSeries);
        this.fitHostHeightToLegend(legend);
      }

      this.root = root;
    });
  }

  private fitHostHeightToLegend(legend: am5.Legend): void {
    const resize = (): void => {
      const hostHeight = legendAwareHostHeight(legend.height());
      if (this.destroyed || hostHeight === this.hostHeight) {
        return;
      }
      this.hostHeight = hostHeight;
      this.changeDetector.detectChanges();
    };
    legend.events.on('boundschanged', resize);
  }

  private axisDateFormats(showYear: boolean): AxisDateFormats {
    const is12Hour = this.dateFormatService.userTimeFormat === DateFnsTimeFormat[TIME_PREF_12H];
    const fullDate = this.dateFormatService.userDateFormat;
    return {
      second: is12Hour ? 'h:mm:ss a' : 'HH:mm:ss',
      hour: is12Hour ? 'h a' : 'HH:mm',
      minute: is12Hour ? 'h:mm a' : 'HH:mm',
      day: showYear ? fullDate : this.dateFormatService.monthDayFormat,
      fullDate,
    };
  }

  private dateAxisLabelFormats(formats: AxisDateFormats, showYear: boolean): DateAxisLabelFormats {
    const monthFormat: DateFormatsByUnit = showYear ? { month: this.dateFormatService.monthYearFormat } : {};
    return {
      dateFormats: {
        second: formats.second,
        minute: formats.minute,
        hour: formats.hour,
        day: formats.day,
        week: formats.day,
        ...monthFormat,
      },
      periodChangeDateFormats: {
        second: formats.second,
        minute: formats.day,
        hour: formats.day,
        day: formats.day,
        week: formats.day,
        ...monthFormat,
      },
    };
  }

  private reserveEdgeLabelRoom(xyChart: am5xy.XYChart, inset: number): void {
    xyChart.leftAxesContainer.set('paddingRight', inset);
    xyChart.set('paddingRight', Math.max(xyChart.get('paddingRight', 0), inset));
  }

  private renderableLabelFormats(labelFormats: DateAxisLabelFormats, units: am5.time.TimeUnit[]): string[] {
    const renderable = units.flatMap((unit) => [labelFormats.dateFormats[unit], labelFormats.periodChangeDateFormats[unit]]);
    return renderable.filter((labelFormat): labelFormat is string => !!labelFormat);
  }

  private valueAxisTitle(chart: AnalystChart): string {
    const title = chart.yAxisLabel ?? '';
    const unit = chart.series.length === 1 ? chart.series[0].unit : '';
    if (!unit || title.includes(unit)) {
      return title;
    }
    return title ? `${title} (${unit})` : unit;
  }

  private seriesTooltipText(useDateAxis: boolean, dateOnly: boolean, formats: AxisDateFormats): string {
    return `${this.tooltipDateHeader(useDateAxis, dateOnly, formats)}{name}: [bold]{valueY}[/]`;
  }

  private tooltipDateHeader(useDateAxis: boolean, dateOnly: boolean, formats: AxisDateFormats): string {
    if (!useDateAxis) {
      return '';
    }
    const dateFormat = dateOnly ? formats.fullDate : `${formats.fullDate} ${formats.minute}`;
    return `{valueX.formatDate('${dateFormat}')}\n`;
  }

  private seriesDisplayName(series: AnalystChart['series'][number]): string {
    return series.unit ? `${series.name} (${series.unit})` : series.name;
  }

  private addSharedTooltipFields(rows: ChartDataRow[], chart: AnalystChart, hiddenSeries: ReadonlySet<number> = new Set()): void {
    for (const row of rows) {
      const entries = chart.series.flatMap((series, index) => {
        const value = row[this.seriesValueKey(index)];
        return hiddenSeries.has(index) || typeof value !== 'number' ? [] : [{ name: this.seriesDisplayName(series), value }];
      });
      if (entries.length === 0) {
        delete row[AnalystChartComponent.SHARED_ANCHOR_KEY];
        delete row[AnalystChartComponent.SHARED_TOOLTIP_TEXT_KEY];
        continue;
      }
      row[AnalystChartComponent.SHARED_ANCHOR_KEY] = Math.max(...entries.map((entry) => entry.value));
      row[AnalystChartComponent.SHARED_TOOLTIP_TEXT_KEY] = buildSharedTooltipText(entries);
    }
  }

  private syncSharedTooltipWithVisibility(
    chart: AnalystChart,
    rows: ChartDataRow[],
    createdSeries: am5xy.XYSeries[],
    anchor: am5xy.LineSeries,
  ): void {
    const refresh = (): void => {
      const hiddenSeries = new Set(createdSeries.flatMap((series, index) => (series.get('visible') === false ? [index] : [])));
      this.addSharedTooltipFields(rows, chart, hiddenSeries);
      anchor.data.setAll(rows);
    };
    createdSeries.forEach((series) => series.on('visible', refresh));
  }

  private addSharedTooltipAnchor(context: SeriesBuildContext, dateHeader: string): am5xy.LineSeries {
    const { root, am5Ns, am5xyNs, xyChart, xAxis, valueAxis, useDateAxis, chartData, lineGapSettings } = context;
    const xField = useDateAxis ? { valueXField: 'x' } : { categoryXField: 'x' };
    const anchor = am5xyNs.LineSeries.new(root, {
      xAxis,
      yAxis: valueAxis,
      valueYField: AnalystChartComponent.SHARED_ANCHOR_KEY,
      ...xField,
      ...lineGapSettings,
      connect: false,
      tooltip: am5Ns.Tooltip.new(root, {
        labelText: `${dateHeader}{${AnalystChartComponent.SHARED_TOOLTIP_TEXT_KEY}}`,
      }),
    });
    anchor.strokes.template.setAll({ visible: false });
    anchor.fills.template.setAll({ visible: false });
    xyChart.series.push(anchor);
    anchor.data.setAll(chartData);
    return anchor;
  }

  private getXValues(chart: AnalystChart): string[] {
    return Array.from(new Set(chart.series.flatMap((series) => series.points.map((point) => point.x))));
  }

  private buildDateChartData(chart: AnalystChart, xValues: string[]): DateChartData | null {
    const rowsByEpoch = new Map<number, ChartDataRow>();

    for (const x of xValues) {
      const epoch = parseAnalystChartDate(x);
      if (epoch === null) {
        return null;
      }
      const row = rowsByEpoch.get(epoch) ?? { x: epoch };
      this.fillSeriesValues(row, chart, x);
      rowsByEpoch.set(epoch, row);
    }

    const ordered = Array.from(rowsByEpoch.entries()).sort(([a], [b]) => a - b);
    return { rows: ordered.map(([, row]) => row), timestamps: ordered.map(([epoch]) => epoch) };
  }

  private buildCategoryChartData(chart: AnalystChart, xValues: string[]): ChartDataRow[] {
    return xValues.map((x) => {
      const row: ChartDataRow = { x };
      this.fillSeriesValues(row, chart, x);
      return row;
    });
  }

  private fillSeriesValues(row: ChartDataRow, chart: AnalystChart, x: string): void {
    chart.series.forEach((series, index) => {
      const point = series.points.find((p) => p.x === x);
      if (point && point.y !== null) {
        row[this.seriesValueKey(index)] = point.y;
      }
    });
  }

  private addSeries(
    context: SeriesBuildContext,
    chartType: AnalystChartType,
    series: AnalystChart['series'][number],
    index: number,
  ): am5xy.XYSeries {
    const { root, am5Ns, am5xyNs, xyChart, xAxis, valueAxis, useDateAxis, tooltipText, sharedTooltip, chartData, lineGapSettings } = context;
    const isLineFamily = chartType !== AnalystChartType.Bar;
    const name = escapeChartMarkup(this.seriesDisplayName(series));
    const tooltipSetting = sharedTooltip ? {} : { tooltip: am5Ns.Tooltip.new(root, { labelText: tooltipText }) };
    const xField = useDateAxis ? { valueXField: 'x' } : { categoryXField: 'x' };

    const amSeries: am5xy.XYSeries = isLineFamily
      ? am5xyNs.LineSeries.new(root, {
          name,
          xAxis,
          yAxis: valueAxis,
          valueYField: this.seriesValueKey(index),
          ...xField,
          ...lineGapSettings,
          connect: false,
          ...tooltipSetting,
        })
      : am5xyNs.ColumnSeries.new(root, {
          name,
          xAxis,
          yAxis: valueAxis,
          valueYField: this.seriesValueKey(index),
          ...xField,
          ...tooltipSetting,
        });

    xyChart.series.push(amSeries);

    if (isLineFamily) {
      const lineSeries = amSeries as am5xy.LineSeries;
      if (chartType === AnalystChartType.Area) {
        lineSeries.fills.template.setAll({ visible: true, fillOpacity: 0.25 });
      } else {
        lineSeries.fills.template.setAll({ visible: false });
      }
      if (chartType === AnalystChartType.Scatter) {
        lineSeries.strokes.template.setAll({ strokeOpacity: 0 });
      }
      const bulletRadius = chartType === AnalystChartType.Scatter ? 4 : 3;
      lineSeries.bullets.push(() => am5Ns.Bullet.new(root, { sprite: am5Ns.Circle.new(root, { radius: bulletRadius }) }));
    }

    amSeries.data.setAll(chartData);
    return amSeries;
  }

  private seriesValueKey(index: number): string {
    return `series${index}`;
  }

  private applyValueAxisDomain(valueAxis: am5xy.ValueAxis<am5xy.AxisRendererY>, chart: AnalystChart): void {
    const values = chart.series.flatMap((series) =>
      series.points.map((point) => point.y).filter((y): y is number => y !== null),
    );
    if (values.length > 0 && Math.min(...values) >= 0) {
      valueAxis.set('min', 0);
    }
  }

  private buildHeatmapChart(
    am5Ns: typeof import('@amcharts/amcharts5'),
    am5xyNs: typeof import('@amcharts/amcharts5/xy'),
    host: HTMLElement,
    chart: AnalystChart,
    heatmap: AnalystChartHeatmap,
  ): am5.Root {
    const is12Hour = this.dateFormatService.userTimeFormat === DateFnsTimeFormat[TIME_PREF_12H];
    const translate = (key: string): string => this.translateService.instant(key);
    const locale = this.translateService.currentLang || this.translateService.defaultLang || undefined;
    const formatLabel = (label: string, kind: AnalystHeatmapCategoryKind | undefined): string =>
      escapeChartMarkup(formatHeatmapCategory(label, kind, is12Hour, translate, locale));
    const xLabels = heatmap.xCategories.map((label) => formatLabel(label, heatmap.xCategoryKind));
    const yLabels = heatmap.yCategories.map((label) => formatLabel(label, heatmap.yCategoryKind));
    const cells: ChartDataRow[] = heatmap.values.flatMap((row, yIndex) =>
      row.flatMap((value, xIndex) => (value === null ? [] : [{ x: xLabels[xIndex], y: yLabels[yIndex], value }])),
    );

    const root = this.chartService.createRoot(host);
    const xyChart = root.container.children.push(am5xyNs.XYChart.new(root, {
      panX: false,
      panY: false,
      layout: root.verticalLayout,
    }));

    const xRenderer = am5xyNs.AxisRendererX.new(root, { minGridDistance: AnalystChartComponent.HEATMAP_X_MIN_GRID_DISTANCE });
    xRenderer.grid.template.set('visible', false);
    const xAxis = xyChart.xAxes.push(am5xyNs.CategoryAxis.new(root, { categoryField: 'x', renderer: xRenderer }));
    const yRenderer = am5xyNs.AxisRendererY.new(root, { inversed: true, minGridDistance: AnalystChartComponent.HEATMAP_MIN_GRID_DISTANCE });
    yRenderer.grid.template.set('visible', false);
    const yAxis = xyChart.yAxes.push(am5xyNs.CategoryAxis.new(root, { categoryField: 'y', renderer: yRenderer }));

    xAxis.children.push(am5Ns.Label.new(root, { text: escapeChartMarkup(chart.xAxisLabel), x: am5Ns.p50, centerX: am5Ns.p50 }));
    yAxis.children.unshift(am5Ns.Label.new(root, { rotation: -90, text: escapeChartMarkup(chart.yAxisLabel), y: am5Ns.p50, centerX: am5Ns.p50 }));
    xAxis.data.setAll(xLabels.map((x) => ({ x })));
    yAxis.data.setAll(yLabels.map((y) => ({ y })));

    const series = xyChart.series.push(am5xyNs.ColumnSeries.new(root, {
      xAxis,
      yAxis,
      categoryXField: 'x',
      categoryYField: 'y',
      valueField: 'value',
      calculateAggregates: true,
    }));
    const backgroundColor = root.interfaceColors.get('background');
    series.columns.template.setAll({
      strokeWidth: 1,
      strokeOpacity: 0.2,
      stroke: backgroundColor,
      tooltipText: `{y} · {x}: [bold]{value}[/]${heatmap.unit ? ` ${escapeChartMarkup(heatmap.unit)}` : ''}`,
      width: am5Ns.percent(100),
      height: am5Ns.percent(100),
    });
    series.set('heatRules', [{
      target: series.columns.template,
      key: 'fill',
      min: backgroundColor,
      max: am5Ns.color(this.commonService.colors.primary),
      dataField: 'value',
    }]);
    series.data.setAll(cells);
    return root;
  }

  private isChartBuildable(chart: AnalystChart | undefined): boolean {
    return !this.destroyed && !!chart && this.hasPoints && !!this.chartDiv;
  }

  private disposeChart(): void {
    this.root?.dispose();
    this.root = undefined;
    if (this.chartDiv) {
      this.chartDiv.nativeElement.innerHTML = '';
    }
  }
}
